import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { onTimeChange } from 'src/app/utils/utils.common';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SharedDataService } from 'src/app/services/app/shared-service/shared-data.service';
import { BillService } from 'src/app/services/core/quickbook/bills/bill.service';
import { VendorService } from 'src/app/services/core/quickbook/vendors/vendor.service';
import { ProductServiceService } from 'src/app/services/core/quickbook/product-services/product-service.service';
import { ActivatedRoute, Router } from '@angular/router';
import { sweetAlert } from 'src/app/utils/swal';
import { getCompanyEmail, getPurchasableProductItems, isPurchasableProductItem } from 'src/app/utils/common.utils';
import * as moment from 'moment';
import { LineItemType } from 'src/app/enums/enums';
import { TitleCasePipe } from '@angular/common';
import { filter, forkJoin, take } from 'rxjs';
import { SpaceTypeService } from 'src/app/services/core/spaceTypes/space-type.service';
import { AddonService } from 'src/app/services/core/addons/addon.service';
import { CompanyService } from 'src/app/services/core/company/company.service';
@UntilDestroy()

@Component({
  selector: 'app-add-bill',
  templateUrl: './add-bill.component.html',
  styleUrls: ['./add-bill.component.css']
})
export class AddBillComponent implements OnInit {

  billForm!: FormGroup;
  billItemForm!: FormGroup;
  submitted = false;
  lineItemFormSubmitted = false;
  preProductItems: any = {};
  billId: any;
  user: any;
  vendors: any;
  productItems: any;
  billData: any;
  previousVendorId = '';
  selectedTrips: any = [];
  showTrips = true;
  tripId: any;
  preSpacetypes: any = {};
  preAddons: any = {};
  companies: any[] = [];
  titleCase = new TitleCasePipe();
  initialDataLoaded = false;
  isSaving = false;
  hasPurchasableItems = true;
  readonly LineItemType = LineItemType;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private productService: ProductServiceService,
    private vendorService: VendorService,
    private billService: BillService,
    private sharedDataService: SharedDataService,
    private spaceTypeService: SpaceTypeService,
    private addonService: AddonService,
    private companyService: CompanyService,
    private fb: FormBuilder) { }

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(untilDestroyed(this)).subscribe((params) => {
        this.billId = params["id"];
        if (this.billId) {
          this.getBillById();
        }
      });

    this.billForm = this.fb.group({
      VendorRef: this.fb.group({
        name: [''],
        value: ['', Validators.required]
      }),
      DueDate: [''],
      BillEmail: this.fb.group({
        Address: ['', [Validators.email]]
      }),
      Line: this.fb.array([], Validators.required),
      reference: this.fb.group({
        name: [''],
        id: ['']
      })
    });
    this.billItemForm = this.createLineItemForm('ItemBasedExpenseLineDetail');
    this.getUser();
  }

  selectedTripsTotal = 0;

  get lineItems() {
    return this.billForm.get('Line') as FormArray;
  }

  getUser() {
    this.sharedDataService.getUser().pipe(
      filter((data) => !!data),
      take(1),
      untilDestroyed(this)
    ).subscribe((data) => {
      if (data) {
        this.user = data;
        const companyEmail = getCompanyEmail(this.user?.company);
        if (companyEmail && !this.billId && !this.billForm.get('BillEmail.Address')?.value) {
          this.billForm.get('BillEmail.Address')?.patchValue(companyEmail);
        }
        if (!this.billId) {
          this.billForm.patchValue({
            DueDate: moment().tz(this.user.timeZone).add(1, 'month').toISOString()
          })
        }
        this.billItemForm.patchValue({
          ItemBasedExpenseLineDetail: {
            ServiceDate: moment().tz(this.user.timeZone).toISOString()
          }
        });
        this.loadInitialData();
      }
    })
  }

  loadInitialData() {
    if (this.initialDataLoaded || this.isSaving) {
      return;
    }
    this.initialDataLoaded = true;
    this.getAllVendors();
    this.getProductItems();
    this.getCompanies();
    const queryParams = {
      limit: 9999,
      isActive: 'true'
    }
    forkJoin({
      spaceTypes: this.spaceTypeService.getPicker(queryParams),
      addons: this.addonService.getAddons(queryParams),
    }).pipe(untilDestroyed(this))
      .subscribe(({ spaceTypes, addons }: any) => {
        if (spaceTypes?.results?.length) {
          this.preSpacetypes = spaceTypes.results.reduce((obj: any, item: any) => (obj[item.id] = item, obj), {});
        }
        if (addons?.results?.length) {
          this.preAddons = addons.results.reduce((obj: any, item: any) => (obj[item.id] = item, obj), {});
        }
      });
  }

  getCompanies() {
    this.companyService
      .getPickerCompanies({ limit: 9999, isActive: true })
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data?.results) {
          this.companies = data.results.sort((a: any, b: any) =>
            (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
          );
        }
      });
  }

  getAllVendors() {
    this.vendorService.getVendors({
      limit: 9999,
      status: 'created'
    })
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data) {
          this.vendors = (data.results || []).filter((vendor: any) => vendor.Id)
        }
      })
  }

  getBillById() {
    this.billService.getBillById(this.billId).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) {
        this.billData = data;
        this.billForm.patchValue(data);
        this.previousVendorId = data.VendorRef?.value || '';
        this.applyBillLines(data.Line);
      }
    });
  }

  applyBillLines(lines: any) {
    const lineItems = this.billForm.get('Line') as FormArray;
    lineItems.clear();
    const fallbackProduct = this.firstPurchasableProduct();
    const defaultServiceDate = this.billData?.DueDate
      || this.billForm.get('DueDate')?.value
      || moment().tz(this.user?.timeZone).toISOString();
    (lines || []).forEach((line: any) => {
      if (line.DetailType === 'SubTotalLineDetail') return;
      const type = line.DetailType === LineItemType.DESCRIPTION_ONLY
        ? LineItemType.DESCRIPTION_ONLY
        : 'ItemBasedExpenseLineDetail';
      const detail = line.ItemBasedExpenseLineDetail;
      const storedItemId = detail?.ItemRef?.value;
      const storedItem = storedItemId ? this.preProductItems[storedItemId] : null;
      const keepStoredItem = !!storedItemId && (!storedItem || isPurchasableProductItem(storedItem));
      const resolvedItem = keepStoredItem ? storedItem : fallbackProduct;
      const itemRefValue = (keepStoredItem ? storedItemId : fallbackProduct?.Id) || '';
      if (type !== LineItemType.DESCRIPTION_ONLY && (itemRefValue == null || itemRefValue === '')) return;
      const lineItem = this.createLineItemForm(type);
      lineItem.patchValue({
        DetailType: type,
        Amount: line.Amount || 0,
        Description: line.Description || '',
        reference: {
          name: line.reference?.name || '',
          id: line.reference?.id || '',
          type: line.reference?.type || ''
        },
        ItemBasedExpenseLineDetail: {
          ItemRef: {
            name: (keepStoredItem ? detail?.ItemRef?.name : '') || resolvedItem?.Name || '',
            value: String(itemRefValue)
          },
          Qty: detail?.Qty || 1,
          UnitPrice: detail?.UnitPrice || line.Amount || 0,
          ServiceDate: detail?.ServiceDate || defaultServiceDate
        }
      });
      lineItems.push(lineItem);
    });
  }

  onSave() {
    this.submitted = true;
    this.billForm.markAllAsTouched();
    if (this.isSaving) return;
    if (this.billForm.invalid) return;

    const payload = this.billForm.value;
    payload.TxnDate = payload.TxnDate || this.billData?.TxnDate || new Date().toISOString();
    if (this.billId && this.billData?.Id) {
      payload.Id = String(this.billData.Id);
    }
    if (this.billId && this.billData?.SyncToken) {
      payload.SyncToken = String(this.billData.SyncToken);
    }
    this.isSaving = true;
    this.billService.saveBill(this.billId, payload).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) {
        this.router.navigateByUrl('/quickbook/bills')
      } else {
        this.isSaving = false;
      }
    }, (err) => {
      this.isSaving = false;
      sweetAlert("Error", err, "error", "Ok");
    });
  }

  onDateChange(controlName: AbstractControl<any, any> | null) {
    onTimeChange(controlName)
  }

  onChangeVendor(value: any) {
    const vendorId = String(value || '');
    const selected = this.vendors?.find((vendor: any) => vendorId == vendor.Id);
    if (!selected) return;
    const vendorEmail = selected?.PrimaryEmailAddr?.Address;
    const companyEmail = getCompanyEmail(this.user?.company) || '';
    const email =
      (typeof vendorEmail === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vendorEmail.trim()))
        ? vendorEmail.trim()
        : companyEmail;
    const patch: any = {
      VendorRef: { name: selected.DisplayName, value: String(selected.Id) },
      BillEmail: { Address: email }
    };
    if (!this.selectedTrips?.length) {
      patch.reference = {
        name: selected.reference?.name || selected.DisplayName || '',
        id: selected.reference?.id || selected.Id || ''
      };
    }
    this.billForm.patchValue(patch);
    this.previousVendorId = vendorId;
  }

  getProductItems() {
    const queryParams = {
      limit: 9999,
      status: 'created'
    }
    this.productService.getProductServices(queryParams)
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data) {
          this.preProductItems = (data.results || []).reduce((obj: any, item: any) => (obj[item.Id] = item, obj), {});
          this.productItems = getPurchasableProductItems(data.results);
          this.hasPurchasableItems = !!this.productItems.length;
          if (this.billData?.Line) {
            this.applyBillLines(this.billData.Line);
          } else if (this.selectedTrips?.length) {
            // Trips may have been selected before products loaded — createLineItemForTrip needs a product
            this.selectedTrips.forEach((trip: any) => this.addTripLineItem(trip));
          }
        }
      })
  }

  onSelectType(event: any) {
    this.billItemForm = this.createLineItemForm(event.target.value);
  }

  onSelectService(event: any) {
    if (!event.target.value) {
      this.clearLineItem();
      return;
    }
    const product = this.preProductItems[event.target?.value];
    if (!product) return;
    const quantity = this.billItemForm.value.ItemBasedExpenseLineDetail?.Qty || 1;
    this.billItemForm.patchValue({
      Description: product.Description || '',
      ItemBasedExpenseLineDetail: {
        ItemRef: { value: event.target.value, name: product.Name || '' },
        Qty: quantity,
        UnitPrice: product.UnitPrice || 0,
        ServiceDate: product.ServiceDate
          ? moment(product.ServiceDate).tz(this.user.timeZone).toISOString()
          : moment().tz(this.user.timeZone).toISOString()
      },
      Amount: this.calculateProductAmount(product.UnitPrice, quantity) || 0,
    });
  }

  addLineItem() {
    this.lineItemFormSubmitted = true;
    if (this.billItemForm.invalid) {
      return;
    }
    const lineItem = this.createLineItemForm(this.billItemForm.value.DetailType);
    lineItem.patchValue({ ...this.billItemForm.value })
    this.lineItems.push(lineItem);

    this.billItemForm = this.createLineItemForm('ItemBasedExpenseLineDetail');
    this.clearLineItem();
    this.lineItemFormSubmitted = false;
  }

  removeLineItem(index: number) {
    const item = this.lineItems.at(index)?.value;
    const tripId = item?.reference?.type === 'trip' ? item?.reference?.id : null;
    this.lineItems.removeAt(index);
    if (tripId) {
      this.setSelectedTrips(this.selectedTrips.filter((t: any) => t.id !== tripId));
      this.updateBillFromSelectedTrips();
    }
  }

  clearAllLineItems() {
    this.lineItems.clear();
  }

  clearLineItem() {
    this.billItemForm.patchValue({
      DetailType: 'ItemBasedExpenseLineDetail',
      Description: '',
      reference: {
        name: '',
        id: '',
        type: ''
      },
      Amount: 0,
      ItemBasedExpenseLineDetail: {
        ItemRef: {
          value: '',
          name: ''
        },
        Qty: 1,
        UnitPrice: 0,
        ServiceDate: moment().tz(this.user.timeZone).toISOString()
      }
    });
  }

  private createLineItemForm(type: any): FormGroup {
    if (type === LineItemType.DESCRIPTION_ONLY) {
      return this.fb.group({
        DetailType: [LineItemType.DESCRIPTION_ONLY],
        Description: ['', Validators.required],
        reference: this.fb.group({
          name: [{ value: '', disabled: true }],
          id: [{ value: '', disabled: true }],
          type: [{ value: '', disabled: true }]
        }),
        Amount: [{ value: 0, disabled: true }],
        ItemBasedExpenseLineDetail: this.fb.group({
          ItemRef: this.fb.group({
            name: [{ value: '', disabled: true }],
            value: [{ value: '', disabled: true }],
          }),
          Qty: [{ value: 0, disabled: true }],
          UnitPrice: [{ value: 0, disabled: true }],
          ServiceDate: [{ value: '', disabled: true }]
        })
      });
    }
    return this.fb.group({
      DetailType: ['ItemBasedExpenseLineDetail'],
      Description: [''],
      reference: this.fb.group({
        name: [''],
        id: [''],
        type: ['']
      }),
      Amount: [0, [Validators.required, Validators.min(0.01)]],
      ItemBasedExpenseLineDetail: this.fb.group({
        ItemRef: this.fb.group({
          name: [''],
          value: ['', Validators.required],
        }),
          Qty: [1, [Validators.required, Validators.min(1)]],
          UnitPrice: [0, [Validators.required, Validators.min(0)]],
          ServiceDate: ['']
      })
    });
  }

  calculateProductAmount(unitPrice: number, quantity: number) {
    return (unitPrice || 0) * (quantity || 0);
  }

  onSelectItem(event: Event, index: number) {
    const selectedId = (event.target as HTMLSelectElement).value;
    const selectedProduct = this.preProductItems[selectedId];
    if (!selectedProduct) return;
    const itemDetail = this.lineItems.at(index).get('ItemBasedExpenseLineDetail') as FormGroup;
    itemDetail.get('ItemRef.name')?.patchValue(selectedProduct.Name || '');
    itemDetail.get('UnitPrice')?.patchValue(selectedProduct.UnitPrice || 0);
    this.onQtyChange(index);
  }

  onQtyChange(index: number) {
    const lineGroup = this.lineItems.at(index) as FormGroup;
    if (lineGroup.get('DetailType')?.value !== 'ItemBasedExpenseLineDetail') {
      return;
    }
    const itemDetail = lineGroup.get('ItemBasedExpenseLineDetail') as FormGroup;
    const qty = parseFloat(itemDetail.get('Qty')?.value) || 0;
    const unitPrice = parseFloat(itemDetail.get('UnitPrice')?.value) || 0;
    lineGroup.get('Amount')?.patchValue(qty * unitPrice);
  }

  firstPurchasableProduct() {
    return (this.productItems || []).find((item: any) => item?.Id);
  }

  createLineItemForTrip(res: any) {
    const product = this.firstPurchasableProduct();
    if (!product?.Id) return null;
    const pickupMoment = res.pickup?.time
      ? moment(res.pickup.time).tz(this.user.timeZone)
      : moment().tz(this.user.timeZone);
    const ServiceDate = pickupMoment?.isValid()
      ? pickupMoment.toISOString()
      : moment().tz(this.user.timeZone).toISOString();
    let UnitPrice = 0;
    let Description = `
Trip Id: ${this.titleCase.transform(res.reference) ?? 'N/A'}
Name: ${this.titleCase.transform(res.member?.firstName ?? '')} ${this.titleCase.transform(res.member?.lastName ?? '')}
Status: ${this.titleCase.transform(res.status ?? 'N/A')}
Spacetype: ${this.titleCase.transform(this.preSpacetypes?.[res.spaceType]?.title ?? 'N/A')}
PU: ${this.titleCase.transform(res.pickup?.address?.name ?? 'N/A')}
DO: ${this.titleCase.transform(res.destination?.address?.name ?? 'N/A')}
Distance: ${res.miles ?? 'N/A'}
`.trim();

    let addonsDescription = '';
    for (const addon of res.addons ?? []) {
      addonsDescription += `${this.titleCase.transform(this.preAddons?.[addon.addon]?.title ?? 'N/A')}(ICD: ${this.titleCase.transform(this.preAddons?.[addon.addon]?.code ?? 'N/A')}) - Quantity: ${addon.quantity ?? 0} \n`;
    }
    if (addonsDescription) {
      Description += '\nAddon:\n' + addonsDescription;
    }

    const offeredFare = res.offeredFare;
    const specialRate = res.specialRate;

    if (res.status === 'completed') {
      UnitPrice = specialRate && specialRate > 0 ? specialRate : offeredFare?.total || 0;
      if (offeredFare) {
        let newFare = { baseFare: offeredFare.baseFare?.fare || 0, perMile: offeredFare.perMile };
        if (offeredFare.zoneBreakdown) {
          newFare.baseFare = offeredFare.zoneBreakdown.baseFare;
          newFare.perMile = offeredFare.zoneBreakdown.fare;
        }
        Description += `\nFare(${this.titleCase.transform(res.status)}): 
       Base Rate (ICD: ${offeredFare.baseFareCode ?? 'N/A'}): ${newFare.baseFare ?? 'N/A'}
       Per Mile Rate (ICD: ${offeredFare.perMileCode ?? 'N/A'}): ${newFare.perMile ?? 'N/A'}
       `;
      }
    } else if (res.status === 'cancelled' && res.flags && res.flags.isBillable) {
      if (res.flags.isNoShow) {
        Description += `\nFare(${res.status}):  
                  No-Show/Dry Run (ICD: ${offeredFare?.dryRunCode ?? 'N/A'}): ${offeredFare?.noShowCharges?.value ?? 'N/A'}
       `;
        UnitPrice = offeredFare?.cancelBreakdown?.noShowCharges || 0;
      } else {
        Description += `\nFare(${res.status}):  
       Cancel Rate: ${offeredFare?.cancelCharges?.value ?? 'N/A'}
       `;
        UnitPrice = offeredFare?.cancelBreakdown?.cancelCharges || 0;
      }
    }

    UnitPrice = Number(UnitPrice) || 0;
    if (offeredFare) {
      Description += `\nOffered Fare: ${offeredFare.total ?? 0} | Special Fare: ${specialRate || 0} | Cancel Rate: ${offeredFare.cancelBreakdown?.cancelCharges ?? 0} | No-Show Rate: ${offeredFare.cancelBreakdown?.noShowCharges ?? 0}`;
      Description += `\nRate Applied(${this.titleCase.transform(res.status)}): ${UnitPrice}`;
    }
    const lineItem = this.createLineItemForm('ItemBasedExpenseLineDetail');
    const productId = product.Id != null ? String(product.Id) : '';
    lineItem.patchValue({
      Description,
      Amount: UnitPrice,
      reference: {
        name: `${res.member?.displayName || ''}-${res.reference || ''}`,
        id: res.id,
        type: 'trip'
      },
      ItemBasedExpenseLineDetail: {
        ItemRef: { value: productId, name: product.Name || '' },
        Qty: 1,
        UnitPrice,
        ServiceDate
      }
    });
    // Select options stringify [value]; keep control as string so the option matches after *ngFor refresh
    lineItem.get('ItemBasedExpenseLineDetail.ItemRef.value')?.setValue(productId);

    return lineItem;
  }

  addTripLineItem(trip: any) {
    if (!trip?.id) return;
    const exists = this.lineItems.controls.some(
      (ctrl: any) => ctrl.get('reference.id')?.value === trip.id
    );
    if (!exists) {
      const lineItem = this.createLineItemForTrip(trip);
      if (lineItem) {
        this.lineItems.push(lineItem);
      }
    }
  }

  removeTripLineItem(tripId: any) {
    if (!tripId) return;
    const index = this.lineItems.controls.findIndex(
      (ctrl: any) => ctrl.get('reference.id')?.value === tripId
    );
    if (index > -1) {
      this.lineItems.removeAt(index);
    }
  }

  updateBillFromSelectedTrips() {
    this.recalculateSelectedTripsTotal();
    if (!this.selectedTrips?.length) {
      const vendorId = this.billForm.get('VendorRef.value')?.value;
      const selected = this.vendors?.find((vendor: any) => vendor.Id == vendorId);
      this.billForm.get('reference')?.patchValue({
        name: selected?.reference?.name || selected?.DisplayName || '',
        id: selected?.reference?.id || selected?.Id || ''
      });
      return;
    }

    const nameReference = this.selectedTrips
      .map((trip: any) => `${trip.member?.displayName || ''}-${trip.reference?.toString() || ''}`)
      .filter(Boolean)
      .join(', ');
    const tripIds = this.selectedTrips
      .map((t: any) => t.id?.toString())
      .filter(Boolean)
      .join(', ');
    this.billForm.get('reference')?.patchValue({
      name: nameReference,
      id: tripIds
    });
  }

  onSelectTrips(event: any) {
    if (!event) return;

    if (event.action === 'toggle') {
      if (event.isSelected) {
        this.addTripLineItem(event.trip);
      } else {
        this.removeTripLineItem(event.trip?.id);
      }
      this.setSelectedTrips(event.selectedTrips || []);
    } else if (event.action === 'selectAll') {
      (event.trips || []).forEach((trip: any) => event.isSelected ? this.addTripLineItem(trip) : this.removeTripLineItem(trip.id));
      this.setSelectedTrips(event.selectedTrips || []);
    } else if (event.action === 'clearAll') {
      (this.selectedTrips || []).forEach((trip: any) => this.removeTripLineItem(trip.id));
      this.setSelectedTrips([]);
    } else if (Array.isArray(event)) {
      this.setSelectedTrips(event);
      const selectedIds = new Set(event.map((t: any) => t.id));
      for (let i = this.lineItems.length - 1; i >= 0; i--) {
        const line = this.lineItems.at(i);
        if (line.get('reference.type')?.value === 'trip' && !selectedIds.has(line.get('reference.id')?.value)) {
          this.lineItems.removeAt(i);
        }
      }
      event.forEach((trip: any) => this.addTripLineItem(trip));
    } else {
      return;
    }
    this.updateBillFromSelectedTrips();
  }

  openTripDetailsModal(tripId: any) {
    this.tripId = tripId;
  }


  /** Sum of the Amount already on each trip line item — display only, no fare logic here. */
  private recalculateSelectedTripsTotal() {
    this.selectedTripsTotal = this.lineItems.controls
      .filter((ctrl: any) => ctrl.get('reference.type')?.value === 'trip')
      .reduce((sum: number, ctrl: any) => sum + (Number(ctrl.get('Amount')?.value) || 0), 0);
  }

  private setSelectedTrips(trips: any[] = []) {
    this.selectedTrips = trips;
  }

}
